// A cited Slack day's messages as an operator reads them: author, time, text, files,
// the cited words marked, a link back to Slack.
import { useEffect, useRef, type ReactNode } from "react";

import type { SlackFile, SlackMessage } from "../api/hooks/useSlack";
import { locateValueInText } from "./textExcerpt";
import styles from "./Slack.module.css";

const formatTime = (iso: string): string =>
  new Date(iso).toLocaleString(undefined, { hour: "2-digit", minute: "2-digit" });

const formatSize = (bytes: number | null): string => {
  if (!bytes) return "";
  return bytes >= 1_000_000
    ? `${(bytes / 1_000_000).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1000))} KB`;
};

const FileLine = ({ file }: { file: SlackFile }) => {
  if (file.voice_note) {
    if (file.transcript_status === "complete" && file.transcript) {
      return (
        <p className={styles.file}>
          Voice note, transcribed: <q>{file.transcript}</q>
        </p>
      );
    }
    return (
      <p className={styles.file}>
        Voice note -{" "}
        {file.transcript_status === "failed"
          ? "could not be transcribed"
          : "transcription pending"}
      </p>
    );
  }
  return (
    <p className={styles.file}>
      File: {file.name}
      {file.size_bytes ? ` (${formatSize(file.size_bytes)})` : ""}
    </p>
  );
};

// The text with the cited words marked, when they can be found in it.
const Highlighted = ({
  text,
  quote,
  markRef,
}: {
  text: string;
  quote: string | null;
  markRef: React.MutableRefObject<HTMLElement | null>;
}): ReactNode => {
  const match = quote ? locateValueInText(text, { evidence: quote }) : null;
  if (!match) return text;
  return (
    <>
      {text.slice(0, match.start)}
      <mark className={styles.mark} ref={markRef}>
        {text.slice(match.start, match.end)}
      </mark>
      {text.slice(match.end)}
    </>
  );
};

export const SlackMessageList = ({
  messages,
  quote,
}: {
  messages: SlackMessage[];
  quote: string | null;
}) => {
  const markRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    markRef.current?.scrollIntoView({ block: "center" });
  }, [quote, messages]);

  if (messages.length === 0) {
    return <p className={styles.empty}>No messages.</p>;
  }
  return (
    <ol className={styles.messages}>
      {messages.map((m) => (
        <li key={m.ts} className={styles.message}>
          <div className={styles.meta}>
            <span className={styles.author}>
              {m.author}
              {m.author_external && <span className={styles.external}> (external)</span>}
            </span>
            <span className={styles.time}>{formatTime(m.posted_at)}</span>
            {m.edited && <span className={styles.time}>(edited)</span>}
            {m.permalink && (
              <a
                className={styles.permalink}
                href={m.permalink}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open in Slack ↗
              </a>
            )}
          </div>
          {m.is_reply && m.parent_excerpt && (
            <p className={styles.parent}>
              Reply to {m.parent_author}: <q>{m.parent_excerpt}</q>
            </p>
          )}
          {m.deleted ? (
            <p className={styles.deleted}>Message deleted</p>
          ) : (
            m.text && (
              <p className={styles.text}>
                <Highlighted text={m.text} quote={quote} markRef={markRef} />
              </p>
            )
          )}
          {m.files.map((f) => (
            <FileLine key={f.file_id} file={f} />
          ))}
        </li>
      ))}
    </ol>
  );
};
